import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, type Book, type Page } from "../api";
import Reader from "./Reader";

type State =
  | { kind: "loading" }
  | { kind: "ok"; book: Book; pages: Page[] }
  | { kind: "unavailable"; reason: "not_found" | "not_ready" };

/**
 * Public, unauthenticated view of a shared book. Mounted outside the auth
 * gate in App.tsx so signed-out visitors can open the link. The book itself
 * renders through the same Reader component the owner uses — only the
 * fallback (archived/deleted/not-yet-ready) is unique to this screen.
 */
export default function Share() {
  const { t } = useTranslation();
  const { shortId } = useParams<{ shortId: string }>();
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    if (!shortId) {
      setState({ kind: "unavailable", reason: "not_found" });
      return;
    }
    let alive = true;
    void api
      .getSharedBook(shortId)
      .then((r) => {
        if (!alive) return;
        if (r.ok) setState({ kind: "ok", book: r.book, pages: r.pages });
        else setState({ kind: "unavailable", reason: r.reason });
      })
      .catch(() => {
        if (alive) setState({ kind: "unavailable", reason: "not_found" });
      });
    return () => {
      alive = false;
    };
  }, [shortId]);

  if (state.kind === "loading") {
    return (
      <div className="tp-app">
        <div className="tp-loading">{t("common.loading")}</div>
      </div>
    );
  }
  if (state.kind === "unavailable") {
    return <Unavailable reason={state.reason} />;
  }
  return <Reader book={state.book} pages={state.pages} trackShortId={state.book.shortId} />;
}

function Unavailable({ reason }: { reason: "not_found" | "not_ready" }) {
  const { t } = useTranslation();
  const heading = reason === "not_ready" ? t("sharePage.notReadyTitle") : t("sharePage.notFoundTitle");
  const body =
    reason === "not_ready"
      ? t("sharePage.notReadyBody")
      : t("sharePage.notFoundBody");
  return (
    <div className="tp-app">
      <div className="tp-screen" style={{ paddingTop: 80, textAlign: "center" }}>
        <div style={{ fontSize: 56, marginBottom: 12 }}>📕</div>
        <div className="tp-display" style={{ marginBottom: 10, fontSize: 32 }}>{heading}</div>
        <div className="tp-body" style={{ color: "var(--tp-ink-soft)", marginBottom: 28, fontSize: 16 }}>
          {body}
        </div>
        <a className="tp-btn tp-btn-block" href="/">
          {t("sharePage.goToApp")}
        </a>
      </div>
    </div>
  );
}
